<template>
  <div class="inspection-module" :dir="lang.dir || 'rtl'">
    <v-card outlined class="mb-3 no-print">
      <v-card-text>
        <b-row class="align-items-end">
          <b-col cols="12" md="4">
            <v-text-field
              v-model="search"
              dense
              outlined
              prepend-inner-icon="mdi-magnify"
              :label="lang.ti_quick_search || 'بحث سريع'"
              hide-details
            />
          </b-col>
          <b-col cols="12" md="3">
            <v-text-field
              v-model="filters.fromDate"
              dense
              outlined
              type="date"
              :label="lang.ti_from_date || 'من تاريخ'"
              hide-details
            />
          </b-col>
          <b-col cols="12" md="3">
            <v-text-field
              v-model="filters.toDate"
              dense
              outlined
              type="date"
              :label="lang.ti_to_date || 'إلى تاريخ'"
              hide-details
            />
          </b-col>
          <b-col cols="12" md="2">
            <v-btn color="primary" block @click="openCreateDialog">
              <v-icon small class="ml-1">mdi-plus</v-icon>
              {{ lang.ti_new_report || 'تقرير جديد' }}
            </v-btn>
          </b-col>
        </b-row>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="filteredReports"
      :search="search"
      item-key="id"
      dense
      class="elevation-1"
      :items-per-page="10"
    >
      <template v-slot:item.date="{ item }">
        <span>{{ item.date }}</span>
      </template>

      <template v-slot:item.vehicle="{ item }">
        <div>
          <div>{{ item.vehicle.type }} - {{ item.vehicle.model }}</div>
          <small class="grey--text">{{ lang.ti_plate || 'لوحة' }}: {{ item.vehicle.plate }}</small>
        </div>
      </template>

      <template v-slot:item.claimant="{ item }">
        <div>
          <div>{{ item.parties.claimant.name }}</div>
          <small class="grey--text">{{ lang.ti_cr || 'س.ت' }}: {{ item.parties.claimant.cr }}</small>
        </div>
      </template>

      <template v-slot:item.defendant="{ item }">
        <div>
          <div>{{ item.parties.defendant.name }}</div>
          <small class="grey--text">{{ lang.ti_cr || 'س.ت' }}: {{ item.parties.defendant.cr }}</small>
        </div>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip x-small dark :color="statusColor(item.status)">
          {{ statusLabel(item.status) }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <div class="d-flex">
          <v-btn small color="blue" dark class="ml-1" @click="openEditDialog(item)">
            <v-icon small class="ml-1">mdi-pencil</v-icon>
            {{ lang.ti_edit || 'تعديل' }}
          </v-btn>
          <v-btn small color="red" dark class="ml-1" @click="askDelete(item)">
            <v-icon small class="ml-1">mdi-delete</v-icon>
            {{ lang.ti_delete || 'حذف' }}
          </v-btn>
          <v-btn small color="green" dark @click="openPreview(item)">
            <v-icon small class="ml-1">mdi-printer</v-icon>
            {{ lang.print || 'طباعة' }}
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="formDialog" max-width="1100" persistent>
      <v-card>
        <v-card-title class="headline">
          {{ isEditMode ? (lang.ti_edit_report_title || 'تعديل تقرير فحص فني') : (lang.ti_add_report_title || 'إضافة تقرير فحص فني') }}
        </v-card-title>
        <v-card-text>
          <v-stepper v-model="step" alt-labels>
            <v-stepper-header>
              <v-stepper-step step="1">{{ lang.ti_step_vehicle || 'معلومات المركبة' }}</v-stepper-step>
              <v-divider />
              <v-stepper-step step="2">{{ lang.ti_step_parties || 'أطراف الدعوى' }}</v-stepper-step>
              <v-divider />
              <v-stepper-step step="3">{{ lang.ti_step_findings || 'النتائج الفنية' }}</v-stepper-step>
              <v-divider />
              <v-stepper-step step="4">{{ lang.ti_step_cost_status || 'التكلفة والحالة' }}</v-stepper-step>
            </v-stepper-header>

            <v-stepper-items>
              <v-stepper-content step="1">
                <b-row>
                  <b-col cols="12" md="4">
                    <v-text-field v-model="form.report_number" dense outlined :label="lang.ti_report_number || 'رقم التقرير'" />
                  </b-col>
                  <b-col cols="12" md="4">
                    <v-text-field v-model="form.date" dense outlined :label="lang.ti_date_label || 'التاريخ (ميلادي/هجري)'" />
                  </b-col>
                  <b-col cols="12" md="4">
                    <v-select
                      v-model="form.status"
                      dense
                      outlined
                      :label="lang.ti_status || 'الحالة'"
                      :items="statusOptions"
                      item-text="text"
                      item-value="value"
                    />
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="12" md="4">
                    <v-text-field v-model="form.vehicle.type" dense outlined :label="lang.ti_vehicle_type || 'نوع المركبة'" />
                  </b-col>
                  <b-col cols="12" md="4">
                    <v-text-field v-model="form.vehicle.model" dense outlined :label="lang.ti_vehicle_model || 'موديل المركبة'" />
                  </b-col>
                  <b-col cols="12" md="4">
                    <v-text-field v-model="form.vehicle.plate" dense outlined :label="lang.ti_plate_number || 'رقم اللوحة'" />
                  </b-col>
                </b-row>
              </v-stepper-content>

              <v-stepper-content step="2">
                <b-row>
                  <b-col cols="12" md="6">
                    <v-card outlined class="pa-3">
                      <div class="font-weight-bold mb-2">{{ lang.ti_claimant || 'المدعي' }}</div>
                      <v-text-field v-model="form.parties.claimant.name" dense outlined :label="lang.ti_claimant_name || 'اسم المدعي'" />
                      <v-text-field v-model="form.parties.claimant.cr" dense outlined :label="lang.ti_commercial_register || 'السجل التجاري'" />
                    </v-card>
                  </b-col>
                  <b-col cols="12" md="6">
                    <v-card outlined class="pa-3">
                      <div class="font-weight-bold mb-2">{{ lang.ti_defendant || 'المدعى عليه' }}</div>
                      <v-text-field v-model="form.parties.defendant.name" dense outlined :label="lang.ti_defendant_name || 'اسم المدعى عليه'" />
                      <v-text-field v-model="form.parties.defendant.cr" dense outlined :label="lang.ti_commercial_register || 'السجل التجاري'" />
                    </v-card>
                  </b-col>
                </b-row>
              </v-stepper-content>

              <v-stepper-content step="3">
                <div class="d-flex justify-space-between align-center mb-2">
                  <div class="font-weight-bold">{{ lang.ti_findings_title || 'المعاينات والنتائج الفنية' }}</div>
                  <v-btn small color="primary" @click="addFinding">
                    <v-icon small class="ml-1">mdi-plus</v-icon>
                    {{ lang.ti_add_item || 'إضافة بند' }}
                  </v-btn>
                </div>

                <v-card outlined class="pa-3" v-for="(finding, index) in form.findings" :key="`finding-${index}`">
                  <b-row class="align-items-center">
                    <b-col cols="11">
                      <v-text-field
                        v-model="finding.fault"
                        dense
                        outlined
                        :label="`${lang.ti_item || 'البند'} ${index + 1}`"
                      />
                    </b-col>
                    <b-col cols="1" class="text-left">
                      <v-btn icon color="error" @click="removeFinding(index)" :disabled="form.findings.length === 1">
                        <v-icon small>mdi-close</v-icon>
                      </v-btn>
                    </b-col>
                  </b-row>
                </v-card>
              </v-stepper-content>

              <v-stepper-content step="4">
                <b-row>
                  <b-col cols="12" md="4">
                    <v-text-field
                      v-model.number="form.labor_cost"
                      dense
                      outlined
                      type="number"
                      :label="lang.ti_labor_cost_sar || 'أجرة العمل (ريال)'"
                    />
                  </b-col>
                  <b-col cols="12" md="8">
                    <v-text-field
                      :value="laborCostInWords"
                      dense
                      outlined
                      readonly
                      :label="lang.ti_labor_cost_words || 'أجرة العمل كتابة'"
                    />
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="12" md="6">
                    <v-textarea
                      v-model="form.status_condition"
                      dense
                      outlined
                      auto-grow
                      rows="3"
                      :label="lang.ti_status_condition || 'وصف الحالة'"
                    />
                  </b-col>
                  <b-col cols="12" md="6">
                    <v-textarea
                      v-model="form.party_to_replay"
                      dense
                      outlined
                      auto-grow
                      rows="3"
                      :label="lang.ti_party_to_replay || 'رد الطرف الثاني'"
                    />
                  </b-col>
                </b-row>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>

        <v-card-actions class="justify-space-between">
          <div>
            <v-btn text color="grey" @click="closeFormDialog">{{ lang.cancel || 'إلغاء' }}</v-btn>
          </div>
          <div>
            <v-btn text :disabled="step === 1" @click="step = Math.max(1, step - 1)">{{ lang.prev || 'السابق' }}</v-btn>
            <v-btn text v-if="step < 4" color="primary" @click="step = Math.min(4, step + 1)">{{ lang.next || 'التالي' }}</v-btn>
            <v-btn color="primary" v-else @click="saveReport">{{ lang.ti_save_report || 'حفظ التقرير' }}</v-btn>
          </div>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="430">
      <v-card>
        <v-card-title class="headline">{{ lang.ti_confirm_delete_title || 'تأكيد الحذف' }}</v-card-title>
        <v-card-text>
          {{ lang.ti_confirm_delete_text || 'هل أنت متأكد من حذف التقرير رقم' }} {{ deleteTarget ? deleteTarget.report_number : '' }} ؟
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="deleteDialog = false">{{ lang.cancel || 'إلغاء' }}</v-btn>
          <v-btn color="error" @click="deleteReport">{{ lang.ti_yes_delete || 'نعم، حذف' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="previewDialog" max-width="1000" scrollable>
      <v-card>
        <v-card-actions class="justify-space-between no-print">
          <v-btn text @click="previewDialog = false">{{ lang.close || 'إغلاق' }}</v-btn>
          <v-btn color="green" dark @click="printReport">
            <v-icon small class="ml-1">mdi-printer</v-icon>
            {{ lang.print || 'طباعة' }}
          </v-btn>
        </v-card-actions>

        <v-card-text class="print-wrapper" v-if="previewReport">
          <div class="official-report" ref="printArea">
            <div class="report-header text-center">
              <h3>{{ lang.ti_report_header || 'تقرير فحص فني' }}</h3>
              <div>{{ lang.ti_office_name || 'شيخ المهنة لتقدير الأضرار' }}</div>
              <div>{{ lang.ti_report_number || 'رقم التقرير' }}: {{ previewReport.report_number }}</div>
            </div>

            <div class="report-box">
              <div class="box-title">{{ lang.ti_section_vehicle || 'أولاً: بيانات المركبة' }}</div>
              <b-row>
                <b-col cols="4"><strong>{{ lang.ti_type || 'النوع' }}:</strong> {{ previewReport.vehicle.type }}</b-col>
                <b-col cols="4"><strong>{{ lang.ti_model || 'الموديل' }}:</strong> {{ previewReport.vehicle.model }}</b-col>
                <b-col cols="4"><strong>{{ lang.ti_plate || 'اللوحة' }}:</strong> {{ previewReport.vehicle.plate }}</b-col>
              </b-row>
              <b-row class="mt-2">
                <b-col cols="12"><strong>{{ lang.ti_date || 'التاريخ' }}:</strong> {{ previewReport.date }}</b-col>
              </b-row>
            </div>

            <div class="report-box">
              <div class="box-title">{{ lang.ti_section_parties || 'ثانياً: أطراف الدعوى' }}</div>
              <b-row>
                <b-col cols="6">
                  <strong>{{ lang.ti_claimant || 'المدعي' }}:</strong> {{ previewReport.parties.claimant.name }}
                  <div>{{ lang.ti_cr || 'س.ت' }}: {{ previewReport.parties.claimant.cr }}</div>
                </b-col>
                <b-col cols="6">
                  <strong>{{ lang.ti_defendant || 'المدعى عليه' }}:</strong> {{ previewReport.parties.defendant.name }}
                  <div>{{ lang.ti_cr || 'س.ت' }}: {{ previewReport.parties.defendant.cr }}</div>
                </b-col>
              </b-row>
            </div>

            <div class="report-box">
              <div class="box-title">{{ lang.ti_section_findings || 'ثالثاً: النتائج الفنية' }}</div>
              <ol>
                <li v-for="(finding, idx) in previewReport.findings" :key="`print-f-${idx}`">
                  {{ finding.fault }}
                </li>
              </ol>
            </div>

            <div class="report-box">
              <p><strong>{{ lang.ti_status_condition || 'وصف الحالة' }}:</strong> {{ previewReport.status_condition || '-' }}</p>
              <p><strong>{{ lang.ti_party_to_replay || 'رد الطرف الثاني' }}:</strong> {{ previewReport.party_to_replay || '-' }}</p>
            </div>

            <div class="report-box">
              <div class="box-title">{{ lang.ti_section_financial || 'رابعاً: التقدير المالي' }}</div>
              <p><strong>{{ lang.ti_labor_cost_numeric || 'أجرة العمل رقماً' }}:</strong> {{ Number(previewReport.labor_cost || 0).toLocaleString('ar-SA') }} {{ lang.currency_code || 'ريال' }}</p>
              <p><strong>{{ lang.ti_labor_cost_words || 'أجرة العمل كتابة' }}:</strong> {{ reportLaborWords }}</p>
            </div>

            <div class="report-signature mt-6">
              <b-row>
                <b-col cols="6" class="text-center">
                  <div>{{ lang.ti_signature_expert || 'خبير الفحص الفني' }}</div>
                  <div class="sign-line"></div>
                  <div>{{ lang.ti_name_signature || 'الاسم والتوقيع' }}</div>
                </b-col>
                <b-col cols="6" class="text-center">
                  <div>{{ lang.ti_signature_approval || 'اعتماد شيخ المهنة' }}</div>
                  <div class="sign-line"></div>
                  <div>{{ lang.ti_name_signature || 'الاسم والتوقيع' }}</div>
                </b-col>
              </b-row>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'TechnicalInspectionReportsModule',
  data() {
    return {
      search: '',
      filters: {
        fromDate: '',
        toDate: ''
      },
      step: 1,
      formDialog: false,
      isEditMode: false,
      deleteDialog: false,
      deleteTarget: null,
      previewDialog: false,
      previewReport: null,
      reports: [],
      form: this.defaultForm()
    };
  },
  created() {
    this.fetchReports();
  },
  computed: {
    lang: function () {
      if (this.$cookies.get(this.$store.state.LangCooki) != null) {
        if (this.$cookies.get(this.$store.state.LangCooki) == 'en') {
          return this.$store.state.lang.en;
        } else if (this.$cookies.get(this.$store.state.LangCooki) == 'ur') {
          return this.$store.state.lang.ur;
        } else {
          return this.$store.state.lang.ar;
        }
      }
      else if (this.$store.state.deflang == 'en') {
        return this.$store.state.lang.en;
      }
      else if (this.$store.state.deflang == 'ur') {
        return this.$store.state.lang.ur;
      } else {
        return this.$store.state.lang.ar;
      }
    },
    headers() {
      return [
        { text: this.lang.ti_report_number || 'رقم التقرير', value: 'report_number', class: 'fit-col', cellClass: 'fit-col' },
        { text: this.lang.ti_date || 'التاريخ', value: 'date', class: 'fit-col', cellClass: 'fit-col' },
        { text: this.lang.ti_vehicle || 'المركبة', value: 'vehicle', sortable: false },
        { text: this.lang.ti_claimant || 'المدعي', value: 'claimant', sortable: false },
        { text: this.lang.ti_defendant || 'المدعى عليه', value: 'defendant', sortable: false },
        { text: this.lang.ti_labor_cost || 'أجرة العمل', value: 'labor_cost', class: 'fit-col', cellClass: 'fit-col' },
        { text: this.lang.ti_status || 'الحالة', value: 'status', class: 'fit-col', cellClass: 'fit-col' },
        { text: this.lang.ti_actions || 'إجراءات', value: 'actions', sortable: false, class: 'actions-col', cellClass: 'actions-col' }
      ];
    },
    statusOptions() {
      return [
        { text: this.lang.ti_status_draft || 'مسودة', value: 'Draft' },
        { text: this.lang.ti_status_issued || 'صادر', value: 'Issued' },
        { text: this.lang.ti_status_final || 'نهائي', value: 'Final' }
      ];
    },
    filteredReports() {
      return this.reports.filter((report) => {
        const reportDate = this.toDateValue(report.date);
        const fromDate = this.toDateValue(this.filters.fromDate);
        const toDate = this.toDateValue(this.filters.toDate);

        const fromOk = fromDate === null || (reportDate !== null && reportDate >= fromDate);
        const toOk = toDate === null || (reportDate !== null && reportDate <= toDate);
        return fromOk && toOk;
      });
    },
    laborCostInWords() {
      return `${this.numberToArabicWords(Number(this.form.labor_cost || 0))} ريال فقط لا غير`;
    },
    reportLaborWords() {
      if (!this.previewReport) return '';
      return `${this.numberToArabicWords(Number(this.previewReport.labor_cost || 0))} ريال فقط لا غير`;
    }
  },
  watch: {
    'filters.fromDate'() {
      this.fetchReports();
    },
    'filters.toDate'() {
      this.fetchReports();
    }
  },
  methods: {
    toDateValue(value) {
      if (!value) return null;
      const raw = String(value).trim().slice(0, 10);
      const parts = raw.split('-');
      if (parts.length !== 3) return null;

      const year = Number(parts[0]);
      const month = Number(parts[1]);
      const day = Number(parts[2]);
      if (!year || !month || !day) return null;

      return Number(`${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`);
    },
    showNotify(type, message, title = '') {
      const notifyTitle = title || (type === 'success' ? (this.lang.ti_success_title || 'نجاح') : (this.lang.alert || 'تنبيه'));
      if (this.$snotify && typeof this.$snotify[type] === 'function') {
        this.$snotify[type](message, notifyTitle, {
          timeout: 3500,
          showProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true
        });
        return;
      }

      if (type === 'error') {
        console.error(message);
      } else {
        console.log(message);
      }
    },
    apiPath() {
      const base =
        this.$store &&
        this.$store.state &&
        this.$store.state.SAMCOTEC &&
        this.$store.state.SAMCOTEC.r_path
          ? this.$store.state.SAMCOTEC.r_path
          : '';

      return base || 'api/index.php';
    },
    buildPostData(type, payload = {}) {
      const post = new FormData();
      post.append('defBranch', localStorage.getItem('defBranch') || '');
      post.append('financeYear', localStorage.getItem('financeYear') || '');
      post.append('auth', this.$cookies.get(this.$store.state.COOKIEPhase) || '');
      post.append('data[lang]', this.$cookies.get(this.$store.state.LangCooki) || '');
      post.append('module', 'technical_inspection_reports');
      post.append('type', type);
      Object.keys(payload).forEach((key) => {
        post.append(`data[${key}]`, payload[key]);
      });
      return post;
    },
    normalizeReportInput(item) {
      const report = JSON.parse(JSON.stringify(item || {}));

      report.id = report.id || null;
      report.report_number = report.report_number || '';
      report.date = report.date || '';
      report.status = report.status || 'Draft';
      report.vehicle = report.vehicle || {};
      report.vehicle.type = report.vehicle.type || '';
      report.vehicle.model = report.vehicle.model || '';
      report.vehicle.plate = report.vehicle.plate || '';
      report.parties = report.parties || {};
      report.parties.claimant = report.parties.claimant || {};
      report.parties.defendant = report.parties.defendant || {};
      report.parties.claimant.name = report.parties.claimant.name || '';
      report.parties.claimant.cr = report.parties.claimant.cr || '';
      report.parties.defendant.name = report.parties.defendant.name || '';
      report.parties.defendant.cr = report.parties.defendant.cr || '';
      report.labor_cost = Number(report.labor_cost || 0);
      report.status_condition = report.status_condition || '';
      report.party_to_replay = report.party_to_replay || '';

      if (!Array.isArray(report.findings) || !report.findings.length) {
        report.findings = [{ fault: '' }];
      } else {
        report.findings = report.findings.map((finding) => ({
          fault: finding && finding.fault ? String(finding.fault) : ''
        }));
      }

      return report;
    },
    toApiPayload(report) {
      return JSON.stringify(this.normalizeReportInput(report));
    },
    async fetchReports() {
      try {
        const post = this.buildPostData('getTechnicalInspectionReports', {
          fromDate: this.filters.fromDate || '',
          toDate: this.filters.toDate || ''
        });
        const response = await axios.post(this.apiPath(), post);
        const rows =
          response &&
          response.data &&
          response.data.results &&
          Array.isArray(response.data.results.data)
            ? response.data.results.data
            : [];

        this.reports = rows.map((row) => this.normalizeReportInput(row));
      } catch (error) {
        this.reports = [];
        this.showNotify('error', this.lang.ti_load_failed || 'تعذر تحميل تقارير الفحص الفني');
        console.warn('Technical inspection reports fetch failed.', error);
      }
    },
    async addReportRequest(report) {
      const post = this.buildPostData('addTechnicalInspectionReport', {
        report: this.toApiPayload(report)
      });
      return axios.post(this.apiPath(), post);
    },
    async editReportRequest(report) {
      const normalized = this.normalizeReportInput(report);
      const post = this.buildPostData('editTechnicalInspectionReport', {
        report_id: normalized.id || '',
        report: JSON.stringify(normalized)
      });
      return axios.post(this.apiPath(), post);
    },
    async deleteReportRequest(reportId) {
      const post = this.buildPostData('deleteTechnicalInspectionReport', {
        report_id: reportId || ''
      });
      return axios.post(this.apiPath(), post);
    },
    defaultForm() {
      return {
        id: null,
        report_number: '',
        date: '',
        vehicle: { type: '', plate: '', model: '' },
        parties: {
          claimant: { name: '', cr: '' },
          defendant: { name: '', cr: '' }
        },
        findings: [{ fault: '' }],
        labor_cost: 0,
        status_condition: '',
        party_to_replay: '',
        status: 'Draft'
      };
    },
    statusLabel(status) {
      const map = {
        Draft: this.lang.ti_status_draft || 'مسودة',
        Issued: this.lang.ti_status_issued || 'صادر',
        Final: this.lang.ti_status_final || 'نهائي'
      };
      return map[status] || status;
    },
    statusColor(status) {
      const map = { Draft: 'grey darken-1', Issued: 'blue darken-2', Final: 'green darken-2' };
      return map[status] || 'grey';
    },
    openCreateDialog() {
      this.isEditMode = false;
      this.step = 1;
      this.form = this.defaultForm();
      this.formDialog = true;
    },
    openEditDialog(item) {
      this.isEditMode = true;
      this.step = 1;
      this.form = JSON.parse(JSON.stringify(item));
      if (!Array.isArray(this.form.findings) || !this.form.findings.length) {
        this.form.findings = [{ fault: '' }];
      }
      this.formDialog = true;
    },
    closeFormDialog() {
      this.formDialog = false;
    },
    addFinding() {
      this.form.findings.push({ fault: '' });
    },
    removeFinding(index) {
      if (this.form.findings.length === 1) return;
      this.form.findings.splice(index, 1);
    },
    async saveReport() {
      const payload = this.normalizeReportInput(this.form);
      if (!payload.report_number || !payload.date || !payload.vehicle.type) return;

      try {
        const wasEdit = !!(this.isEditMode && payload.id);
        if (this.isEditMode && payload.id) {
          await this.editReportRequest(payload);
        } else {
          await this.addReportRequest(payload);
        }

        await this.fetchReports();
        this.showNotify('success', wasEdit
          ? (this.lang.ti_update_success || 'تم تحديث التقرير بنجاح')
          : (this.lang.ti_add_success || 'تم إضافة التقرير بنجاح')
        );
      } catch (error) {
        this.showNotify('error', this.lang.ti_save_failed || 'تعذر حفظ التقرير');
        console.warn('Technical inspection report save failed.', error);
      }

      this.formDialog = false;
    },
    askDelete(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },
    async deleteReport() {
      if (!this.deleteTarget) return;

      const reportId = this.deleteTarget.id;
      try {
        await this.deleteReportRequest(reportId);
        await this.fetchReports();
        this.showNotify('success', this.lang.ti_delete_success || 'تم حذف التقرير بنجاح');
      } catch (error) {
        this.showNotify('error', this.lang.ti_delete_failed || 'تعذر حذف التقرير');
        console.warn('Technical inspection report delete failed.', error);
      }

      this.deleteDialog = false;
      this.deleteTarget = null;
    },
    openPreview(item) {
    //   this.previewReport = JSON.parse(JSON.stringify(item));
    //   this.previewDialog = true;
        window.open(`${window.location.origin}/api/print.php?treport_id=${item.id}`, '_blank');
    },
    printReport() {
      window.print();
    },
    numberToArabicWords(num) {
      const number = Math.floor(Number(num || 0));
      if (number === 0) return 'صفر';

      const units = ['', 'واحد', 'اثنان', 'ثلاثة', 'أربعة', 'خمسة', 'ستة', 'سبعة', 'ثمانية', 'تسعة'];
      const teens = ['عشرة', 'أحد عشر', 'اثنا عشر', 'ثلاثة عشر', 'أربعة عشر', 'خمسة عشر', 'ستة عشر', 'سبعة عشر', 'ثمانية عشر', 'تسعة عشر'];
      const tens = ['', '', 'عشرون', 'ثلاثون', 'أربعون', 'خمسون', 'ستون', 'سبعون', 'ثمانون', 'تسعون'];
      const hundreds = ['', 'مائة', 'مائتان', 'ثلاثمائة', 'أربعمائة', 'خمسمائة', 'ستمائة', 'سبعمائة', 'ثمانمائة', 'تسعمائة'];

      const convertTriplet = (n) => {
        const h = Math.floor(n / 100);
        const rem = n % 100;
        const parts = [];

        if (h > 0) parts.push(hundreds[h]);

        if (rem > 0) {
          if (rem < 10) {
            parts.push(units[rem]);
          } else if (rem < 20) {
            parts.push(teens[rem - 10]);
          } else {
            const u = rem % 10;
            const t = Math.floor(rem / 10);
            if (u > 0) parts.push(`${units[u]} و ${tens[t]}`);
            else parts.push(tens[t]);
          }
        }

        return parts.join(' و ');
      };

      let n = number;
      const parts = [];

      const millions = Math.floor(n / 1000000);
      if (millions > 0) {
        if (millions === 1) parts.push('مليون');
        else if (millions === 2) parts.push('مليونان');
        else if (millions <= 10) parts.push(`${convertTriplet(millions)} ملايين`);
        else parts.push(`${convertTriplet(millions)} مليون`);
        n %= 1000000;
      }

      const thousands = Math.floor(n / 1000);
      if (thousands > 0) {
        if (thousands === 1) parts.push('ألف');
        else if (thousands === 2) parts.push('ألفان');
        else if (thousands <= 10) parts.push(`${convertTriplet(thousands)} آلاف`);
        else parts.push(`${convertTriplet(thousands)} ألف`);
        n %= 1000;
      }

      if (n > 0) {
        parts.push(convertTriplet(n));
      }

      return parts.join(' و ');
    }
  }
};
</script>

<style scoped>
.inspection-module {
  direction: rtl;
}

.official-report {
  width: 100%;
  margin: 0 auto;
  background: #fff;
  color: #111;
  border: 1px solid #000;
  padding: 16px;
}

.report-header {
  border-bottom: 2px solid #000;
  padding-bottom: 10px;
  margin-bottom: 12px;
}

.report-box {
  border: 1px solid #000;
  padding: 10px;
  margin-bottom: 10px;
}

.box-title {
  font-weight: 700;
  margin-bottom: 8px;
}

.sign-line {
  border-bottom: 1px solid #000;
  margin: 22px 20px 10px;
  height: 1px;
}

.inspection-module ::v-deep .actions-col {
  width: 1%;
  white-space: nowrap;
}

.inspection-module ::v-deep .fit-col {
  width: 1%;
  white-space: nowrap;
}

.inspection-module ::v-deep td.actions-col {
  min-width: max-content;
}

.inspection-module ::v-deep td.fit-col,
.inspection-module ::v-deep th.fit-col {
  min-width: max-content;
}

@media print {
  body * {
    visibility: hidden;
  }

  .official-report,
  .official-report * {
    visibility: visible;
  }

  .official-report {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    width: 100%;
    border: none;
  }

  .no-print {
    display: none !important;
  }
}
</style>
